import React, { useState } from 'react';
import { MessageSquare, Send, Sparkles, Bot } from 'lucide-react';
import { WaitlistForm } from '../components/WaitlistForm';
import { trackEvent } from '../lib/analytics';

type Message = {
  role: 'user' | 'assistant';
  content: string;
};

const exampleQueries = [
  "Vilka bidrag finns tillgängliga för cleantech-startups?",
  "Hur skriver jag en effektiv projektbeskrivning för Vinnova?",
  "Finns det bidrag för AI-projekt inom hälsovård?",
  "Vad krävs för att söka EU-bidrag för mitt projekt?"
];

function Demo() {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hej! Jag är din Utlysningslabbet AI-assistent. Jag kan hjälpa dig hitta och ansöka om bidrag som passar just ditt projekt. Vad vill du veta mer om?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    trackEvent('demo_message_send', { message: input });

    // Add user message
    const userMessage = { role: 'user' as const, content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const response = {
        role: 'assistant' as const,
        content: `Detta är en demo-version av Utlysningslabbet AI. I den färdiga versionen kommer jag att kunna hjälpa dig genom hela processen: researcha relevanta utlysningar baserat på din profil, analysera framgångsrika ansökningar inom din sektor, föreslå rätt nyckelord och formuleringar, bevaka deadlines, och guida dig steg för steg genom ansökningsprocessen. Jag kommer även att kunna ge konkreta exempel från liknande projekt som fått finansiering och hjälpa dig förstå exakt vad olika finansiärer letar efter.`
      };
      setMessages(prev => [...prev, response]);
      setIsTyping(false);
    }, 1500);
  };

  const handleExampleClick = (query: string) => {
    trackEvent('example_query_click', { query });
    setInput(query);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center space-x-2 text-blue-600 mb-4">
            <Sparkles size={20} />
            <span className="text-sm font-semibold">Demo Version</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Upplev kraften i Utlysningslabbet
          </h1>
          <div className="space-y-4">
            <p className="text-lg text-gray-600">
              Föreställ dig en AI-assistent som analyserar ditt projekt och matchar det mot hundratals aktiva bidrag från ledande finansiärer. Med insikter från tusentals framgångsrika ansökningar guidar vi dig genom hela processen - från att hitta rätt utlysning till att skicka in en konkurrenskraftig ansökan.
            </p>
            <p className="text-lg text-gray-600">
              Detta är en förhandstitt på framtidens bidragsansökning - där AI gör det enklare än någonsin att hitta och söka finansiering för dina innovativa idéer.
            </p>
          </div>
        </div>
      </section>

      {/* Chat Interface */}
      <section className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-6">
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === 'assistant' ? 'items-start' : 'items-start justify-end'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center mr-4 mt-1">
                      <Bot size={20} className="text-blue-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'assistant'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-600 text-white'
                    }`}
                  >
                    <p className="text-[15px] leading-relaxed">{message.content}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                    <Bot size={20} className="text-blue-600" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Example Queries */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">Prova något av dessa exempel:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQueries.map((query, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(query)}
                  className="text-sm bg-white px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200"
                >
                  {query}
                </button>
              ))}
            </div>
          </div>

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                className="flex-1 min-w-0 px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="shrink-0 bg-blue-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span className="hidden sm:inline">Skicka</span>
                <Send size={18} />
              </button>
            </div>
          </form>
        </div>

        {/* Beta Access CTA */}
        <div className="mt-8 bg-gray-900 rounded-2xl p-6 md:p-8">
          {!submitted ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-4 text-center">
                Vill du bli en av de första att få tillgång till tjänsten?
              </h2>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto text-center">
                Gå med i väntelistan för att få tidig tillgång till Utlysningslabbet AI och var bland de första
                att revolutionera hur du söker bidrag.
              </p>
              <div className="max-w-md mx-auto">
                <WaitlistForm 
                  onSuccess={() => setSubmitted(true)} 
                  buttonText="Gå med i väntelistan"
                />
              </div>
            </>
          ) : (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Tack för ditt intresse!
              </h2>
              <p className="text-gray-300">
                Vi håller dig uppdaterad om projektets utveckling.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Demo;